import React from 'react';
import {
    Provider,
    connect,
    useSelector
} from 'react-redux';
import PropTypes from 'prop-types';
import { unstable_HistoryRouter as Router } from 'react-router-dom';
import StyledEngineProvider from '@mui/material/StyledEngineProvider';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material';
import { Helmet } from 'react-helmet';
import { injectIntl, intlShape } from 'react-intl';
import classnames from 'classnames';

import appStore from './appStore';
import CustomIntlProvider from '../intl/CustomIntlProvider';
import FlowRoute from '../routing/FlowRoute';
import { history } from '../routing/routing';
import Header from '../header/HeaderContainer';
import NewHeaderContainer from '../header/NewHeaderContainer';
import headerMessages from '../header/message/HeaderMessage';
import Footer from '../footer/Footer';
import NewFooter from '../footer/NewFooter';
import Loader from '../loader/loader';
import { getLoaderStatus } from '../loader/loaderSelector';
import { getIsWebView } from '../webView/webViewSelectors';
import ScrollToTopWrapper from '../primitive/ScrollToTopWrapper';
import { getEnv, getIsOldUiPreferred } from './bootstrap';
import { ConnectedTestEnvironmentBanner } from '../testEnvironment/TestEnvironmentBanner';
import { getBrandName } from '../brand/brandSelectors';

import {
    XS_BREAKPOINT_CUTOFF, SM_BREAKPOINT_CUTOFF,
    MD_BREAKPOINT_CUTOFF, LG_BREAKPOINT_CUTOFF,
    XL_BREAKPOINT_CUTOFF
} from '../utils/deviceTypeUtils';
import LocalePickerPopup from '../localeCurrencyPicker/LocalePickerPopup';
import { FlowProvider } from '../routing/FlowProvider';


const theme = createTheme({
    breakpoints: {
        values: {
            xs: XS_BREAKPOINT_CUTOFF,
            sm: SM_BREAKPOINT_CUTOFF,
            md: MD_BREAKPOINT_CUTOFF,
            lg: LG_BREAKPOINT_CUTOFF,
            xl: XL_BREAKPOINT_CUTOFF
        }
    }
});

let App = (props) => {
    const {
        intl,
        brandName,
        isLoading
    } = props;
    const enableNewUI = !getIsOldUiPreferred();
    const isWebView = useSelector(state => getIsWebView(state));
    const showTestEnv = getEnv() !== 'PROD';

    const [isOpen, setIsOpen] = React.useState(false);
    const [clickLocation, setClickLocation] = React.useState('Header');

    const callbackIsOpen = (isOpenStatus) => {
        setIsOpen(isOpenStatus);
    };

    const popupStatus = (isOpenStatus) => {
        setIsOpen(isOpenStatus);
    };

    const handleClickLocation = (location) => {
        setClickLocation(location);
    };

    
    const mainRef = React.useRef(null);

    
    const handleSkipToMain = (e) => {
        e.preventDefault();
        if (mainRef.current) {
            mainRef.current.focus();
        }
    };

    return (
        <div>
            <StyledEngineProvider injectFirst>
                {}
                <a
                    href="#main-content"
                    onClick={handleSkipToMain}
                    className="skip-to-content"
                >
                    {intl.formatMessage(headerMessages.skipToMainContent)}
                </a>
                <div className="App">
                    <Helmet>
                        {  }
                        <title>{intl.formatMessage(headerMessages.pageTitle, { brandName })}</title>
                        <meta name="title" content={intl.formatMessage(headerMessages.pageTitle, { brandName })} />
                        <meta name='description' content={intl.formatMessage(headerMessages.pageDescription, { brandName })} />
                        {  }
                        {  }
                        <meta property="og:title" content={intl.formatMessage(headerMessages.pageTitle, { brandName })} />
                        <meta property="og:description" content={intl.formatMessage(headerMessages.pageDescription, { brandName })} />
                        {  }
                        {  }
                        <meta name="twitter:title" content={intl.formatMessage(headerMessages.pageTitle, { brandName })} />
                        <meta name="twitter:description" content={intl.formatMessage(headerMessages.pageDescription, { brandName })} />
                        {  }
                    </Helmet>
                    {}
                    <header className="header-wrapper">
                        {showTestEnv && <ConnectedTestEnvironmentBanner />}
                        {!isWebView
                            && (enableNewUI ? (
                                <FlowProvider>
                                    <NewHeaderContainer
                                        callbackIsOpen={callbackIsOpen}
                                        handleClickLocation={handleClickLocation}
                                        isDialogOpen={isOpen}
                                    />
                                </FlowProvider>
                            ) : (
                                <Header callbackIsOpen={callbackIsOpen} />
                            ))}
                    </header>
                    <main
                        id="main-content"
                        ref={mainRef}
                        tabIndex="-1"
                        className={classnames('app-body', { 'app-body-v2': enableNewUI })}
                    >
                        {isLoading && <Loader showTestEnv={showTestEnv} />}
                        <FlowRoute />
                    </main>
                    {enableNewUI ? <NewFooter callbackIsOpen={callbackIsOpen} handleClickLocation={handleClickLocation} /> : <Footer />}
                    <LocalePickerPopup isOpen={isOpen} popupStatus={popupStatus} clickLocation={clickLocation} />
                </div>
            </StyledEngineProvider>
        </div>
    );
};

App.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    brandName: PropTypes.string.isRequired
};

export const mapStateToProps = state => ({
    brandName: getBrandName(state),
    isLoading: getLoaderStatus(state)
});

App = connect(mapStateToProps)(injectIntl(App));

const ReduxApp = () => (
    <Provider store={appStore}>
        <CustomIntlProvider>
            <ThemeProvider theme={theme}>
                <Router history={history}>
                    {}
                    <ScrollToTopWrapper>
                        <App />
                    </ScrollToTopWrapper>
                </Router>
            </ThemeProvider>
        </CustomIntlProvider>
    </Provider>
);


export default ReduxApp;
